import { useEffect, useState } from "react";
import {
  claimRingtone,
  isRingtoneAllowed,
  releaseRingtone,
  setLocalRingback,
  subscribeRingState,
} from "../lib/ringCoordinator";

/**
 * Publish this tab's ringback state so the ringtone can stand down — D7.
 *
 * Call it from wherever the ringback itself is bound (the outgoing ringing
 * route). Suppression within the same tab is immediate: the coordinator holds
 * `localRingback` in memory and notifies synchronously, so the banner never gets
 * a chance to make a sound. Other tabs learn over the channel.
 */
export function useRingbackClaim(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    setLocalRingback(true);
    return () => setLocalRingback(false);
  }, [active]);
}

/**
 * Decide whether *this* tab is the one that rings for `inviteId` — D7 + D8.
 *
 * Returns false while a ringback is playing anywhere (D7) or while an older tab
 * has claimed the same invite (D8). The caller still renders its banner either
 * way: the AC is "the banner appears but stays silent", not "the banner is
 * hidden" — a silent tab must still be answerable.
 *
 * Claiming is optimistic, so in the ordinary single-tab case the ringtone starts
 * with no delay at all. See `../lib/ringCoordinator.ts` for why yielding is
 * driven by challenges from live tabs rather than by a tally of claims.
 */
export function useRingtoneElection(inviteId: string | null, wanted: boolean): boolean {
  const [allowed, setAllowed] = useState(isRingtoneAllowed);

  useEffect(() => subscribeRingState(() => setAllowed(isRingtoneAllowed())), []);

  useEffect(() => {
    if (!wanted || !inviteId) return;
    claimRingtone(inviteId);
    return () => releaseRingtone();
  }, [inviteId, wanted]);

  return wanted && allowed;
}
